import { BACKEND } from "@lib/api"
import { dmSans125ClassName } from "@lib/fonts"
import { cn } from "@lib/utils"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@ui/components/dialog"
import { Loader2, Plus } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import { brainConnectorIcon } from "../brain-connector-icons"
import { ConnectorCard } from "../directory/connector-card"
import { PillButton } from "../integrations/install-steps"

const MCP_BASE = `${BACKEND}/brain/mcp-connections`

type NangoApp = { integration: string; provider?: string }
type IngestStatus = {
	job: { enabled: number; last_error?: string | null } | null
	channels: Array<{ name: string; backfill_done: number; messages: number; chunks: number; last_error?: string | null }>
}

const APP_NAMES: Record<string, string> = { "discord-bot": "Discord" }
const prettyName = (key: string) =>
	APP_NAMES[key] ?? key.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")

const DAY_PRESETS = [30, 90, 180, 365]

type Estimate = {
	channels: number
	skippedPrivate: number
	messages: number
	tokens: number
	costLow: number
	costHigh: number
}

const money = (n: number) => (n < 0.01 ? "under $0.01" : `$${n.toFixed(n < 1 ? 2 : 2)}`)
const muted = cn(dmSans125ClassName(), "text-[12px] font-medium text-[#737373]")

/** Apps connected through Nango, shown as their own section with per-app actions. */
export function NangoSection({ isAdmin }: { isAdmin: boolean }) {
	const [apps, setApps] = useState<NangoApp[] | null>(null)
	const [adding, setAdding] = useState(false)
	const [ingest, setIngest] = useState<IngestStatus | null>(null)
	const [importOpen, setImportOpen] = useState(false)
	const [days, setDays] = useState(90)
	const [starting, setStarting] = useState(false)
	const [estimate, setEstimate] = useState<Estimate | null>(null)
	const [estimating, setEstimating] = useState(false)
	const [estimateError, setEstimateError] = useState<string | null>(null)

	const loadApps = useCallback(async () => {
		const res = await fetch(`${MCP_BASE}/nango/connections`, { credentials: "include" }).catch(() => null)
		const data = res?.ok ? ((await res.json()) as { connections?: NangoApp[] }) : { connections: [] }
		setApps(data.connections ?? [])
	}, [])

	const loadIngest = useCallback(async () => {
		if (!isAdmin) return
		const res = await fetch(`${MCP_BASE}/discord-ingest`, { credentials: "include" }).catch(() => null)
		if (res?.ok) setIngest((await res.json()) as IngestStatus)
	}, [isAdmin])

	useEffect(() => {
		void loadApps()
		void loadIngest()
		// Connecting in the Nango tab doesn't notify us; refresh when the user comes back.
		const onFocus = () => {
			void loadApps()
			void loadIngest()
		}
		window.addEventListener("focus", onFocus)
		return () => window.removeEventListener("focus", onFocus)
	}, [loadApps, loadIngest])

	const running = Boolean(ingest?.job?.enabled) && (ingest?.channels ?? []).some((c) => c.backfill_done === 0)
	useEffect(() => {
		if (!running) return
		const timer = setInterval(() => void loadIngest(), 8000)
		return () => clearInterval(timer)
	}, [running, loadIngest])

	useEffect(() => {
		if (!importOpen || !Number.isFinite(days) || days < 1) return
		setEstimating(true)
		setEstimateError(null)
		const timer = setTimeout(() => {
			void fetch(`${MCP_BASE}/discord-ingest/estimate`, {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ days }),
			})
				.then(async (res) => {
					const data = (await res.json().catch(() => ({}))) as Estimate & { error?: string }
					if (!res.ok) throw new Error(data.error ?? "Couldn't estimate.")
					setEstimate(data)
				})
				.catch((e: Error) => {
					setEstimate(null)
					setEstimateError(e.message)
				})
				.finally(() => setEstimating(false))
		}, 500)
		return () => clearTimeout(timer)
	}, [importOpen, days])

	const addApp = async (shared: boolean) => {
		setAdding(true)
		try {
			const res = await fetch(`${MCP_BASE}/nango/connect`, {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ shared }),
			})
			const data = (await res.json().catch(() => ({}))) as { connectLink?: string; error?: string }
			if (!res.ok || !data.connectLink) {
				toast.error(data.error ?? "Couldn't open Nango.")
				return
			}
			window.open(data.connectLink, "_blank", "noopener")
		} finally {
			setAdding(false)
		}
	}

	const startImport = async () => {
		setStarting(true)
		try {
			const res = await fetch(`${MCP_BASE}/discord-ingest/start`, {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ days }),
			})
			const data = (await res.json().catch(() => ({}))) as { found?: number; skippedPrivate?: number; error?: string }
			if (!res.ok) {
				toast.error(data.error ?? "Couldn't start the import.")
				return
			}
			toast.success(`Importing ${data.found ?? 0} public channels (${data.skippedPrivate ?? 0} private skipped).`)
			setImportOpen(false)
			await loadIngest()
		} finally {
			setStarting(false)
		}
	}

	const stopImport = async () => {
		await fetch(`${MCP_BASE}/discord-ingest/stop`, { method: "POST", credentials: "include" })
		await loadIngest()
	}

	const discordLine = (): string => {
		const channels = ingest?.channels ?? []
		if (!ingest?.job) return "Reads live when asked. Import history to add it to memory."
		const messages = channels.reduce((n, c) => n + c.messages, 0)
		const done = channels.filter((c) => c.backfill_done === 1).length
		const state = !ingest.job.enabled ? "Import stopped" : done < channels.length ? "Importing" : "Up to date"
		const err = ingest.job.last_error ? ` · ${ingest.job.last_error.slice(0, 60)}` : ""
		return `${state} · ${messages.toLocaleString()} messages · ${done}/${channels.length} channels${err}`
	}

	return (
		<section className="flex flex-col gap-3">
			<div className="flex items-end justify-between gap-3">
				<div>
					<p className={cn(dmSans125ClassName(), "font-semibold text-[14px] text-[#FAFAFA]")}>Nango</p>
					<p className={muted}>Apps connected through Nango. Your brain reads them when asked.</p>
				</div>
				<PillButton onClick={() => void addApp(false)} disabled={adding}>
					{adding ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
					Add an app
				</PillButton>
			</div>

			{apps === null ? (
				<p className={muted}>Loading…</p>
			) : apps.length === 0 ? (
				<p className={muted}>No apps yet. Click “Add an app”, then pick one in the Nango window.</p>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{apps.map((app) => {
						const isDiscord = app.integration === "discord-bot"
						return (
							<ConnectorCard
								key={app.integration}
								name={prettyName(app.integration)}
								subtitle={isDiscord ? discordLine() : "Connected through Nango."}
								icon={brainConnectorIcon(app.integration, prettyName(app.integration))}
								footerLeft={
									<span className={cn(muted, "flex items-center gap-1.5")}>
										<span className="size-1.5 rounded-full bg-emerald-400" />
										Connected
									</span>
								}
								footerRight={
									isDiscord && isAdmin ? (
										<div className="flex gap-2">
											{running ? (
												<PillButton onClick={() => void stopImport()}>Stop import</PillButton>
											) : null}
											<PillButton onClick={() => setImportOpen(true)}>
												{ingest?.job ? "Re-import" : "Import history"}
											</PillButton>
										</div>
									) : null
								}
							/>
						)
					})}
				</div>
			)}

			<Dialog open={importOpen} onOpenChange={setImportOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Import Discord history</DialogTitle>
					</DialogHeader>
					<p className={muted}>
						Reads past messages from every channel that everyone in the server can see and adds them to shared memory,
						so anyone in this workspace can ask about them. Private channels are skipped. It keeps running in the
						background and then picks up new messages.
					</p>
					<div className="flex flex-wrap items-center gap-2">
						{DAY_PRESETS.map((d) => (
							<PillButton key={d} onClick={() => setDays(d)}>
								{d === days ? "● " : ""}
								{d} days
							</PillButton>
						))}
						<label className={cn(muted, "ml-1 flex items-center gap-2")}>
							Custom
							<input
								type="number"
								min={1}
								max={3650}
								value={Number.isFinite(days) ? days : ""}
								onChange={(e) => setDays(e.target.value === "" ? Number.NaN : Math.min(3650, Math.floor(Number(e.target.value))))}
								className="h-8 w-20 rounded-lg bg-[#0B0F15] px-2 text-[#FAFAFA] outline-none"
							/>
							days
						</label>
					</div>
					<div className={cn(muted, "rounded-lg bg-[#0B0F15] p-3 leading-5")}>
						{estimating ? (
							<span className="flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" />Sampling your channels…</span>
						) : estimateError ? (
							estimateError
						) : estimate ? (
							<>
								About <b className="text-[#FAFAFA]">{estimate.messages.toLocaleString()}</b> messages from{" "}
								{estimate.channels} channels (about {(estimate.tokens / 1000).toFixed(estimate.tokens < 10000 ? 1 : 0)}k tokens).
								<br />
								Estimated memory cost: <b className="text-[#FAFAFA]">{money(estimate.costLow)}–{money(estimate.costHigh)}</b> on
								supermemory ($5–10 per 1M tokens; the free plan includes $5 a month). Discord and Nango add nothing.
								<br />
								An estimate from each channel's recent activity, so busy channels may come in lower.
							</>
						) : (
							"Enter a number of days to see an estimate."
						)}
					</div>
					<div className="flex justify-end">
						<PillButton onClick={() => void startImport()} disabled={starting || !Number.isFinite(days) || days < 1}>
							{starting ? <Loader2 className="size-3.5 animate-spin" /> : null}
							Start import
						</PillButton>
					</div>
				</DialogContent>
			</Dialog>
		</section>
	)
}
